import { SPEC_FILE_NAME } from "@/constants";
import { useHandler } from "@/hooks";
import type {
  FpFileCreated,
  FpFileUpdated,
  FpVersionCreated,
} from "../../../schemas/codegen";
import { useMessageStore } from "../../state/stores";

const isSpecFile = (filename: string) => filename.endsWith(SPEC_FILE_NAME);

// Import the constant from the message store
const EPHEMERAL_VERSION_KEY = "ephemeral";

export function useAttachmentManagement() {
  const updateMessage = useMessageStore((state) => state.updateMessage);

  const handleFileCreated = useHandler((event: FpFileCreated) => {
    const { messageId, file } = event;

    const isSpec = isSpecFile(file.filename);
    if (isSpec) {
      // For spec files, just update the spec
      updateMessage(messageId, { spec: file });
      return;
    }

    // We need to get the message from the store directly because the messages list otherwise might not yet be updated
    // In case this handler gets called from another handler
    const messages = useMessageStore.getState().messages;
    const message = messages.find((m) => m.id === messageId);

    // For regular files, update the version with the new file
    const currentVersion = message?.version;
    updateMessage(messageId, {
      version: {
        id: currentVersion?.id ?? EPHEMERAL_VERSION_KEY,
        files: currentVersion?.files
          ? [
              ...currentVersion.files.filter(
                (item) => item.filename !== file.filename,
              ),
              file,
            ]
          : [file],
        version: currentVersion?.version ?? 1,
        specId: message?.spec?.id ?? currentVersion?.specId ?? "",
        createdAt: currentVersion?.createdAt ?? new Date(),
        updatedAt: new Date(),
      },
    });
  });

  const handleFileUpdated = useHandler((event: FpFileUpdated) => {
    const { messageId, file } = event;

    const isSpec = isSpecFile(file.filename);
    if (isSpec) {
      updateMessage(messageId, { spec: file });
      return;
    }

    // We need to get the message from the store directly because the messages list otherwise might not yet be updated
    // In case this handler gets called from another handler
    const messages = useMessageStore.getState().messages;
    const message = messages.find((m) => m.id === messageId);

    const currentVersion = message?.version;
    updateMessage(messageId, {
      version: {
        id: currentVersion?.id ?? EPHEMERAL_VERSION_KEY,
        files: currentVersion?.files?.map((f) => {
          // Match by both ID and filename to handle cases where IDs might not match
          // (e.g., when files are created via tool-call results vs. backend updates)
          if (f.id === file.id || f.filename === file.filename) {
            return file;
          }
          return f;
        }) ?? [file],
        version: currentVersion?.version ?? 1,
        specId: message?.spec?.id ?? currentVersion?.specId ?? "",
        createdAt: currentVersion?.createdAt ?? new Date(),
        updatedAt: new Date(),
      },
    });
  });

  const handleVersionCreated = useHandler((event: FpVersionCreated) => {
    const { messageId, version } = event;
    const files = version.files;

    // First update the message with the new version
    updateMessage(messageId, { version: { ...version, files: [] } });

    for (const file of files) {
      // Then update the message with the new files
      handleFileCreated({
        type: "agent.file.created",
        messageId,
        file,
      });
    }
  });

  return {
    handleFileCreated,
    handleFileUpdated,
    handleVersionCreated,
  };
}
